import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import DatePicker from "react-datepicker";
import { FiCalendar } from "react-icons/fi";
import { IoMdDownload } from "react-icons/io";
import ChartLoader from "../../molecules/ChartLoader";
import {
  CacheWithExpiration,
  getFirstLastDateOfYear,
} from "../../../lib/utils/funcs";
import {
  GoogleReviewResponse,
  DownloadRequestResponse,
} from "../../../lib/utils/types";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import {
  getGoogleReviewAnalytics,
  downloadGoogleReviewsClicked,
} from "../../../lib/apis/analytics";
import ChartError from "../../molecules/ChartError";
import { toast } from "react-toastify";

interface ClickGoogleReviewGraphProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?:boolean;
  googleStats?: any;
  onDownloadRequested?: (response: DownloadRequestResponse) => void;
}

const ClickGoogleReviewGraph: React.FC<ClickGoogleReviewGraphProps> = ({ hospitalId, startDate, endDate, report, googleStats, onDownloadRequested }) => {
  const { hospitalID: contextHospitalID } = useHospDocData();
  const effectiveHospitalId = hospitalId || contextHospitalID;
  // If props provided, use them, else use internal state
  const [internalYear, setInternalYear] = useState<number>(new Date().getFullYear());
  const effectiveYear = startDate && endDate ? new Date(startDate).getFullYear() : internalYear;
  const [googleReviewData, setGoogleReviewData] = useState<GoogleReviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update handlers to use internal state only if not in external mode
  const isExternal = !!(hospitalId && startDate && endDate);
  const handleYearChange = (date: Date | null) => {
    if (!isExternal && date) setInternalYear(date.getFullYear());
  };

  const fetchGoogleReviewDataWithCache = async () => {
    if (!effectiveHospitalId) return;
    setError(null);
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 500);
    const { start_date, end_date } = getFirstLastDateOfYear(effectiveYear);
    const cacheKey = `google-review-${effectiveHospitalId}-${start_date}-${end_date}`;
    try {
      const cachedData = CacheWithExpiration.get(cacheKey);
      if (cachedData) {
        setGoogleReviewData(cachedData);
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        return;
      }
      const data = await getGoogleReviewAnalytics({
        hospital_id: effectiveHospitalId,
        start_date,
        end_date,
      });
      CacheWithExpiration.set(cacheKey, data, 60);
      setGoogleReviewData(data);
    } catch (error) {
      setError("Failed to load chart data. Please try again.");
    } finally {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    fetchGoogleReviewDataWithCache();
  };

  const handleDownloadGoogleReviewsClicked = async () => {
    if (!effectiveHospitalId || !effectiveYear) return;
    const start_date = `${effectiveYear}-01-01`;
    const end_date = `${effectiveYear}-12-31`;
    const params = {
      hospital_id: effectiveHospitalId,
      start_date,
      end_date,
    };
    try {
      const response = await downloadGoogleReviewsClicked(params);
      if (onDownloadRequested) {
        onDownloadRequested(response);
      } else {
        toast.success("Download request submitted successfully");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to request download");
    }
  };

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    if (effectiveHospitalId && effectiveYear && !googleStats) {
      fetchGoogleReviewDataWithCache();
    }else if(googleStats){
      setGoogleReviewData(googleStats)
    }
  }, [effectiveHospitalId, effectiveYear]);

  // Prepare chart data
  const chartSeries = [
    {
      data: googleReviewData?.map((item) => item.count) || [],
    },
  ];
  const categories = googleReviewData?.map((item) => item.month) || [];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    colors: ["#06d6a0"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: categories,
    },
    tooltip: {
      y: {
        title: {
          formatter: () => "Reviews Link Clicked",
        },
      },
    },
  };

  return (
    <div className="w-full  bg-white rounded-lg shadow flex flex-col justify-center p-4">
      <div className={`flex flex-col gap-2 sm:flex-row justify-between items-center ${report ? "hidden" : ""}`}>
        <h2 className="hidden md:flex text-lg font-semibold text-gray-700 ">
          Google Reviews Given
        </h2>
        <div className="flex md:hidden flex-row justify-between w-full">
        <h2 className="flex text-lg font-semibold text-gray-700 ">
          Google Reviews Given
        </h2>
        <IoMdDownload className="w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadGoogleReviewsClicked} />
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <DatePicker
              selected={new Date(effectiveYear, 0)}
              onChange={handleYearChange}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full  md:w-[100px]"
              dateFormat="yyyy"
              showYearPicker
              placeholderText="Select year"
              maxDate={new Date()}
              minDate={new Date("2023-01-01")}
              calendarClassName="border-none shadow-lg !text-sm !flex !justify-center !text-center"
            />

            <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
          </div>
          <div className="flex">
            {" "}
            <IoMdDownload className="hidden md:flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadGoogleReviewsClicked} />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && <ChartLoader height={350} containerClassName="" />}

      {/* Error State */}
      {error && !isLoading && (
        <ChartError
          error={error}
          onRetry={handleRetry}
          containerClassName="border-t border-gray-100"
          height={350}
        />
      )}

      {!isLoading && !error && (
        <ReactApexChart
          options={options}
          series={chartSeries}
          type="bar"
          height={350}
        />
      )}
    </div>
  );
};

export default ClickGoogleReviewGraph;
